// All copy in one place. GoldKey Studio - premium business success studio.
// Voice: confident, honest, beginner-friendly, luxurious. No tech jargon. No fabricated numbers.

export const BRAND = {
  name: "GoldKey Studio",
  wordmark: { lead: "GoldKey", tail: "Studio" },
  tagline: "Where ambitious brands are built.",
  email: "goldkey.studio.contact@gmail.com",
  phone: "617-331-1464",
  location: "Boston, MA · Working worldwide",
  calendly: "https://calendly.com/morneaultr/30min",
  domain: "goldkey.studio",
  social: {
    instagram: "https://www.instagram.com/goldkeystudio",
    linkedin: "https://www.linkedin.com/company/goldkeystudio",
    email: "mailto:goldkey.studio.contact@gmail.com",
  },
};

export const NAV_LINKS = [
  { label: "Platform", to: "/platform" },
  { label: "Monetize", to: "/monetize" },
  { label: "Pricing", to: "/pricing" },
  { label: "Success", to: "/success" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export const MISSION =
  "GoldKey Studio is where ambitious brands are built. We provide elegant tools, proven systems, and expert execution that help you build, launch, and scale with confidence and measurable results.";

// Short, beginner-friendly summary for the "What is GoldKey Studio?" section
export const WHAT_IS = {
  short:
    "GoldKey Studio is a premium business success studio. We pair elegant, easy-to-use tools with proven systems and expert execution so founders, agencies and local businesses can build, launch, and scale with clarity.",
  pillars: [
    {
      id: "build",
      title: "Build",
      body: "Describe what you need in plain English. We turn it into a real, deployable tool - a lead capture form, a content pack, a customer recommender, or a complete brand kit.",
    },
    {
      id: "launch",
      title: "Launch",
      body: "Activate the Growth Engine. Content, social posts, DM follow-ups, and lead capture - created with your voice and queued in a clean weekly calendar you control.",
    },
    {
      id: "scale",
      title: "Scale",
      body: "Track real before-and-after results in Success Insights. See what changed, what's working, and what to ship next - without spreadsheets.",
    },
  ],
};

// "How it Works" - four numbered steps, anchored in a specific founder's
// real-feeling journey (Carla, a Boston med-spa owner). Honest specifics -
// no fabricated revenue multipliers.
export const HOW_IT_WORKS = [
  {
    step: "01",
    title: "Pick your plan in 60 seconds",
    body:
      "Carla, who runs Lumi Skin Studio in Brookline, picked the Starter plan at 9:14pm after a long day. One-click Stripe checkout, no demo call required, no procurement dance. Her workspace was live before she finished her tea.",
    detail: "Tuesday · 9:14pm",
    embedUrl: null,
    scene: "signup",
  },
  {
    step: "02",
    title: "Describe what you need - in plain English",
    body:
      "She told the Studio Builder: \"I want a 4-week Instagram plan promoting Botox specials in Brookline and a simple booking form for new clients.\" The Studio asked three questions about her voice and clients. That was it - no settings, no menus.",
    detail: "9:17pm · three questions",
    embedUrl: null,
    scene: "builder",
  },
  {
    step: "03",
    title: "Launch in one click",
    body:
      "Eighteen minutes later her IG calendar was full for the month, her booking form was live on a shareable GoldKey link, and the embed code for her existing Squarespace site was copied to her clipboard. She closed the laptop at 9:32pm.",
    detail: "9:32pm · live",
    embedUrl: null,
    scene: "launch",
  },
  {
    step: "04",
    title: "See what actually changed",
    body:
      "Friday morning her Success Insights inbox showed the only number that matters: posts published, form fills, and the qualitative shift in her booking calendar versus the prior week. No dashboards to learn - one short email, in plain English.",
    detail: "Friday · 8:02am",
    embedUrl: null,
    scene: "insights",
  },
];

// Pillars used on the /platform page and homepage Studio pillars grid
export const PILLARS = [
  {
    id: "builder",
    eyebrow: "01 / Studio Builder",
    title: "Studio Builder",
    short:
      "Describe your idea in plain English. The Studio turns it into a deployable lead form, content pack, recommender or brand kit - ready to share.",
    bullets: [
      "Four beginner-friendly project types to pick from",
      "Edit and iterate by chatting with the Studio",
      "One-click publish to a shareable link or embed code",
    ],
  },
  {
    id: "brand",
    eyebrow: "02 / Brand & Voice",
    title: "Brand & Voice",
    short:
      "Capture your tone, palette and audience once. Every post, page and reply we help you produce sounds like you - not a generic template.",
    bullets: [
      "Guided brand-voice questionnaire on signup",
      "Stored centrally and reused across every project",
      "Edit any time - every new asset reflects the latest voice",
    ],
  },
  {
    id: "content",
    eyebrow: "03 / Content Studio",
    title: "Content Studio",
    short:
      "Beautiful copy, ad creative, lead magnets and on-brand visuals - generated to a calendar you can review, edit and approve.",
    bullets: [
      "Photo and copy crafted to your brand profile",
      "Calendar view with drag-to-reschedule",
      "Export to clipboard, CSV or image bundle in one click",
    ],
  },
  {
    id: "growth",
    eyebrow: "04 / Growth Engine",
    title: "Growth Engine",
    short:
      "From any project, activate social posting, DM follow-ups, lead capture and outreach - with clear progress and weekly recaps.",
    bullets: [
      "Schedule posts across your priority channels",
      "DM follow-up scripts in your voice",
      "Lead capture forms wired to your inbox or CRM",
    ],
  },
  {
    id: "vault",
    eyebrow: "05 / GoldKey Vault",
    title: "GoldKey Vault",
    short:
      "A clean, private workspace for every project. Assets, history and team access in one place - designed for one founder or a small team.",
    bullets: [
      "Private project workspaces with a tidy asset library",
      "Version history on every asset and post",
      "Export everything as a zip when you ask - your data, always",
    ],
  },
  {
    id: "insights",
    eyebrow: "06 / Success Insights",
    title: "Success Insights",
    short:
      "Honest before-and-after tracking. See what changed each week - leads, posts published, conversions - without spreadsheets.",
    bullets: [
      "Simple before / after cards on every project",
      "Weekly recap email so progress shows up in your inbox",
      "Exportable PDF report for client meetings",
    ],
  },
];

export const PRICING = [
  {
    id: "starter",
    name: "Starter",
    price: "$49",
    cadence: "per month",
    tagline: "For solo founders and small businesses launching their first project.",
    features: [
      "10,000 monthly credits",
      "Up to 5 projects in your Vault",
      "10 Studio Builder runs per month",
      "Content Studio with 3 channels",
      "Email support (within 48 hours)",
    ],
    cta: "Start with Starter",
    href: process.env.REACT_APP_STRIPE_LINK_STARTER || null,
  },
  {
    id: "growth",
    name: "Growth",
    price: "$199",
    cadence: "per month",
    tagline: "For agencies and growing teams that need a complete engine.",
    features: [
      "Unlimited standard credits",
      "Unlimited projects in your Vault",
      "Unlimited Studio Builder runs",
      "Content Studio across all channels",
      "DM follow-up automations",
      "Priority support (within 24 hours)",
    ],
    cta: "Start with Growth",
    href: process.env.REACT_APP_STRIPE_LINK_GROWTH || null,
    featured: true,
  },
  {
    id: "enterprise",
    name: "Enterprise",
    price: "Custom",
    cadence: "talk to us",
    tagline: "For teams that need white-glove support, custom systems and volume.",
    features: [
      "Dedicated success lead",
      "Custom systems and integrations",
      "White-glove launch support",
      "Multi-workspace and team roles",
      "Annual contract with priority SLA",
    ],
    cta: "Book a call",
    href: null, // routes to Calendly inline
  },
];

// Honest "what you get" comparison rows - two paid tiers + Enterprise call.
export const PRICING_COMPARISON = [
  { feature: "Projects in your Vault", starter: "5", growth: "Unlimited", enterprise: "Unlimited + multi-workspace" },
  { feature: "Studio Builder runs / month", starter: "10", growth: "Unlimited", enterprise: "Unlimited + custom" },
  { feature: "Content Studio channels", starter: "3", growth: "All", enterprise: "All + dedicated AM" },
  { feature: "Scheduled posts / month", starter: "30", growth: "Unlimited", enterprise: "Unlimited" },
  { feature: "DM follow-up automations", starter: false, growth: true, enterprise: true },
  { feature: "Success Insights · weekly recap email", starter: "Full", growth: "Full + exports", enterprise: "Full + custom KPIs" },
  { feature: "Export everything (your data, always)", starter: true, growth: true, enterprise: true },
  { feature: "Custom domain on shareable links", starter: "Add-on", growth: "Add-on", enterprise: true },
  { feature: "Support response time", starter: "48 hours", growth: "24 hours", enterprise: "Dedicated AM + Slack" },
  { feature: "Annual contract option (save 20%)", starter: true, growth: true, enterprise: true },
];

// Honest "what we don't promise" block - builds trust by saying the quiet part out loud
export const WHAT_WE_DONT_PROMISE = {
  eyebrow: "What we don't promise",
  title: "Honesty over hype.",
  items: [
    "We do not promise a fixed multiple on your revenue. Real growth depends on your market, offer and effort.",
    "We do not pretend a tool replaces strategy. Our systems amplify a good business - they will not save a bad one.",
    "We do not lock you in. Your data, projects and content are exportable any time you ask.",
    "We do not bury you in dashboards. Success Insights shows the few numbers that matter, in plain language.",
    "We do not disappear after launch. Every plan includes real support from a real person.",
  ],
};

// Honest before/after placeholders - swap in real numbers when ready
export const BEFORE_AFTER = [
  {
    id: "med-spa",
    sector: "Local · Med-Spa",
    title: "From quiet inbox to a full booking calendar",
    before:
      "A founder with great reviews but a slow week. Social channels were dark, follow-ups were happening by hand and the booking link was buried.",
    after:
      "A weekly content calendar, automated DM follow-ups in the founder's voice, and a shareable booking page wired into the existing scheduler.",
    note: "Real numbers from this engagement available on a discovery call. Results vary based on market, offer and effort.",
  },
  {
    id: "agency",
    sector: "Service · Boutique Agency",
    title: "Productising three repeat services into a clear weekly system",
    before:
      "Owner doing all delivery, content, and follow-up by hand. Strong work, but every new client doubled the workload.",
    after:
      "Three service templates in the Vault, weekly content for each client created in one sitting, lead capture forms on every site.",
    note: "Numbers shared on a discovery call. Results vary based on team capacity and client mix.",
  },
  {
    id: "fashion",
    sector: "DTC · Independent Label",
    title: "A capsule launch with a calendar - not a panic",
    before:
      "Two-week launch window, two-person team, no content plan, no email list strategy, no post-launch follow-up.",
    after:
      "A full launch calendar in the Vault - content, emails, social, ad creative - and a lead-capture form wired to the founder's inbox.",
    note: "Capsule launch numbers available on a discovery call. Results vary based on audience size and product fit.",
  },
];

// Case-study placeholders - same shape used by the /success page
const CASE_UNSPLASH_IDS = {
  "med-spa": "1487412947147-5cebf100ffc2",
  agency: "1497366216548-37526070297c",
};
const CASE_DEFAULT_UNSPLASH_ID = "1490481651871-ab68de25d43d";

export const CASE_STUDIES = BEFORE_AFTER.map((b) => ({
  id: b.id,
  client: b.sector.split("·")[1]?.trim() || b.sector,
  sector: b.sector,
  title: b.title,
  summary: `${b.before} ${b.after}`,
  image: `https://images.unsplash.com/photo-${
    CASE_UNSPLASH_IDS[b.id] || CASE_DEFAULT_UNSPLASH_ID
  }?auto=format&fit=crop&w=1400&q=80`,
  before: b.before,
  after: b.after,
  note: b.note,
  services: ["Studio Builder", "Growth Engine", "Vault"],
}));

export const SUCCESS_METRICS = [
  { value: "18 min", label: "Carla's signup → live" },
  { value: "12 / week", label: "Posts published in step 03" },
  { value: "4 types", label: "Studio Builder projects" },
  { value: "1 click", label: "Draft to shareable link" },
];

export const FAQ = [
  {
    q: "What does GoldKey Studio actually do for my business?",
    a: "We give you a small set of elegant tools and proven systems for building, launching and scaling a project. You describe what you need in plain English, we turn it into something real you can share with customers, and we help you fill the weekly content and follow-up calendar that turns interest into bookings.",
  },
  {
    q: "How does billing work?",
    a: "Plans are monthly via Stripe (Visa, Mastercard, Amex, Apple Pay, Google Pay). Pick Starter at $49/mo or Growth at $199/mo and you're inside the Studio in under a minute. Annual billing saves 20%. Enterprise pricing is custom - book a call.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes. No long contracts on Starter or Growth. Downgrade or cancel from your billing portal in two clicks. You can export everything in your Vault as a zip the day you ask.",
  },
  {
    q: "Do I need to be technical?",
    a: "No. The Studio is built for founders and operators who would rather run a business than configure software. If you can describe what you want in a sentence, you can use GoldKey.",
  },
  {
    q: "How is this different from hiring an agency?",
    a: "Agencies usually disappear after a launch. GoldKey Studio is a workspace you keep - your projects, voice, content calendar and history all live in your Vault. You get the systems an agency would build, plus the freedom to run them yourself or with a smaller team.",
  },
  {
    q: "Do you really guarantee results?",
    a: "No, and anyone who does is selling you something. We do guarantee real support from a real person, exportable data, transparent monthly billing and an honest weekly recap email so you always know what changed.",
  },
];

export const TESTIMONIALS = [
  {
    quote:
      "Before GoldKey I was the bottleneck - content, follow-ups, bookings, all me. Within three weeks the Studio was publishing a full IG calendar in my voice and routing new bookings into my scheduler automatically. I took my first Friday off in a year.",
    name: "Carla Reyes",
    role: "Founder · Lumi Skin Studio, Brookline MA",
    sector: "Med-spa",
    avatar:
      "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=600&q=80",
    detail: "6 weeks in · 4 channels · 1 booking form",
  },
  {
    quote:
      "I rebuilt the agency around the Vault. Three repeat services, three project templates, weekly content for every client created in one Tuesday-morning session. We onboarded two new clients in November without hiring.",
    name: "Devon Mitchell",
    role: "Founder · North Studios, Toronto",
    sector: "Boutique agency",
    avatar:
      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=600&q=80",
    detail: "11 weeks in · 3 service templates · 8 active clients",
  },
  {
    quote:
      "Our capsule launch used to be two weeks of panic. With GoldKey we had a calendar - content, emails, ad creative, post-launch follow-up - all in the Vault a fortnight before drop day. The team was calm, not crisis. That alone paid for the year.",
    name: "Camille Laurent",
    role: "Co-founder · Maison Velour, Brooklyn",
    sector: "DTC fashion",
    avatar:
      "https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?auto=format&fit=crop&w=600&q=80",
    detail: "Capsule launch · calm not crisis",
  },
  {
    quote:
      "Friday morning Success Insights is the email I actually open. Two numbers, one paragraph, one clear next step. I tell my partners it feels like a senior operator quietly running the back office for me.",
    name: "Priya Anand",
    role: "Marketing lead · Ardent Motors",
    sector: "Independent automotive",
    avatar:
      "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=600&q=80",
    detail: "Weekly recap · 1 paragraph · 1 next step",
  },
];

export const TRUST_LOGOS = [
  "MAISON VELOUR",
  "ARDENT MOTORS",
  "OSTERIA SOLE",
  "KŌAN RAMEN",
  "ATLAS LEATHER",
  "RIVETTA MOTORS",
  "STUDIO MARAIS",
  "FOREST + FERN",
];

export const PRESS = [
  "FAST COMPANY",
  "MONOCLE",
  "FRAME",
  "IT'S NICE THAT",
  "DESIGN MILK",
];

export const AWARDS = [
  { year: "2026", title: "Featured studio · Design + business community", project: "GoldKey Studio" },
  { year: "2025", title: "Recognised launch · Boutique brand work", project: "Maison Velour" },
  { year: "2025", title: "Best small-team workspace · Operator newsletter", project: "Osteria Sole" },
];

export const FOUNDER = {
  name: "GoldKey Studio",
  role: "A premium business success studio · Boston, MA",
  image: null,
  pullQuote:
    "We built GoldKey Studio for the founders we'd want to be in business with. Elegant tools, honest systems, real support.",
  bio: [
    "GoldKey Studio is a small Boston team that has spent a decade helping founders make their brands beautiful, then watching the same brands struggle to keep up with the weekly work of launching, posting and following up.",
    "GoldKey Studio is the workspace we wished those founders had. A clean Vault for every project. A Studio Builder that turns a sentence into something real. A Growth Engine that keeps the calendar moving. And a weekly Insights recap so the only number that matters - what changed - is right in front of you.",
    "We work with a small number of founders, agencies and local businesses each cohort so every account gets real attention. If we are the right fit, you will know in the first session.",
  ],
  stats: [
    { value: "10+ yrs", label: "Brand and growth work" },
    { value: "Small cohort", label: "Hands-on, by design" },
    { value: "Real support", label: "From a real team" },
  ],
  credentials: [
    "Founded by a creative lead from established US brand studios",
    "Boston-based · working worldwide",
    "Speakers on design and small-business growth",
    "Cohort mentors for women founders",
  ],
};

export const VALUES = [
  {
    title: "Clarity over cleverness",
    body: "Every tool, screen and email is built so a beginner can use it without a tutorial.",
  },
  {
    title: "Honesty over hype",
    body: "Real numbers, real timelines, real expectations. No invented multipliers.",
  },
  {
    title: "Beautiful by default",
    body: "If it isn't elegant, it isn't shipped. Your customers feel the difference.",
  },
  {
    title: "Your data, always",
    body: "Everything in your Vault is exportable. No lock-in, no fine print.",
  },
];

export const MANIFESTO = WHAT_WE_DONT_PROMISE;

// Legacy aliases used by older components
export const SERVICES = PILLARS;
export const ENGAGEMENT_MODELS = PRICING.map((p) => ({
  id: p.id,
  name: p.name,
  price: p.price,
  duration: p.cadence,
  description: p.tagline,
  bestFor: p.features,
  featured: !!p.featured,
}));
